import { Bell, Bot, ChevronRight, Menu, RefreshCw, X } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { navigation, stateLabel } from "../data";
import type { OpsSnapshot, ViewId } from "../types";
import { useKstClock } from "../hooks/useOps";

interface ShellProps {
  view: ViewId;
  snapshot: OpsSnapshot;
  loading: boolean;
  onNavigate: (view: ViewId) => void;
  onRefresh: () => void;
  children: ReactNode;
}

export function Shell({ view, snapshot, loading, onNavigate, onRefresh, children }: ShellProps) {
  const [open, setOpen] = useState(false);
  const { date, time } = useKstClock();
  const current = navigation.find((item) => item.id === view) ?? navigation[0];
  const problemCount = snapshot.sources.filter((source) => source.state !== "connected").length;

  const navigate = (next: ViewId) => {
    onNavigate(next);
    setOpen(false);
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "is-open" : ""}`}>
        <div className="brand">
          <div className="brand-mark">YF</div>
          <div>
            <strong>YURIMFOOD</strong>
            <span>통합운영센터</span>
          </div>
          <button className="sidebar-close" aria-label="메뉴 닫기" onClick={() => setOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <p className="nav-label">주요 업무</p>
        <nav className="main-nav" aria-label="주요 업무">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={view === item.id ? "active" : ""}
                onClick={() => navigate(item.id)}
              >
                <span className="nav-number">{item.number}</span>
                <Icon size={18} strokeWidth={1.8} />
                <span className="nav-text">{item.label}</span>
                {item.id === "system" && problemCount > 0 ? (
                  <span className="nav-alert">{problemCount}</span>
                ) : (
                  <ChevronRight className="nav-chevron" size={15} />
                )}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-status">
          <div className="status-title">
            <span className={`status-dot ${snapshot.state}`} />
            운영 서비스 연결
          </div>
          <strong>{stateLabel[snapshot.state]}</strong>
          <span>마지막 상태 확인 {time}</span>
        </div>
      </aside>

      {open && <button className="sidebar-backdrop" aria-label="메뉴 닫기" onClick={() => setOpen(false)} />}

      <main className="main-area">
        <header className="topbar">
          <div className="topbar-title">
            <button className="mobile-menu" aria-label="메뉴 열기" onClick={() => setOpen(true)}>
              <Menu size={22} />
            </button>
            <div className="mobile-brand">YF</div>
            <div>
              <span>{current.eyebrow}</span>
              <h1>{current.title}</h1>
            </div>
          </div>

          <div className="topbar-actions">
            <div className="clock">
              <span>{date}</span>
              <strong>{time}</strong>
            </div>
            <button className={`connection-pill ${snapshot.state}`} onClick={onRefresh}>
              <span className={`status-dot ${snapshot.state}`} />
              {loading ? "상태 확인 중" : stateLabel[snapshot.state]}
              <RefreshCw size={14} className={loading ? "spin" : ""} />
            </button>
            <button className="assistant-button">
              <span className="ai-mark">AI</span>
              운영비서
            </button>
            <button className="bell-button" aria-label="알림">
              <Bell size={18} />
              {problemCount > 0 && <span>{problemCount}</span>}
            </button>
            <button className="user-button" aria-label="사용자 메뉴">대표</button>
          </div>
        </header>

        <section className="page-content">{children}</section>
      </main>

      <nav className="mobile-dock" aria-label="모바일 주요 메뉴">
        {(["control", "live", "sales", "settlement"] as ViewId[]).map((id) => {
          const item = navigation.find((entry) => entry.id === id)!;
          const Icon = item.icon;
          return (
            <button key={id} className={view === id ? "active" : ""} onClick={() => navigate(id)}>
              <Icon size={20} />
              <span>{item.label}</span>
            </button>
          );
        })}
        <button className="dock-ai" aria-label="AI 운영비서">
          <Bot size={21} />
          <span>AI 질문</span>
        </button>
      </nav>
    </div>
  );
}

